import React from 'react';
import { NavigationContext } from '../../../context/NavigationContext';
import PdfStatusSection from './PdfStatusSection';
import ExportActionsSection from './ExportActionsSection';

interface RtdKonto {
    id: number;
    accountName: string;
    institute: string;
    iban: string;
    bic: string;
    glaeubigerId: string;
}

interface ProjectFile {
    id: number;
    filepath: string;
    projectId: number;
}

interface Project {
    id: number;
    name: string;
    description: string | null;
    status: string;
    projectFiles?: ProjectFile[];
    rtdKontoId: number | null;
    rtdKonto?: RtdKonto;
}

interface ProjectDetailsHeaderProps {
    project: Project;
    token: string;
    onProjectUpdate: (updatedProject: Project) => void;
    refreshTrigger?: number;
}

const ProjectDetailsHeader: React.FC<ProjectDetailsHeaderProps> = ({
    project,
    token,
    onProjectUpdate,
    refreshTrigger
}) => {
    const navigationContext = React.useContext(NavigationContext);
    
    if (!navigationContext) {
        throw new Error('ProjectDetailsHeader must be used within a NavigationProvider');
    }

    const { navigateTo } = navigationContext;
    const [isEditing, setIsEditing] = React.useState(false);
    const [editedName, setEditedName] = React.useState('');
    const [editedStatus, setEditedStatus] = React.useState('');
    const [editedRtdKontoId, setEditedRtdKontoId] = React.useState<number | null>(null);
    const [rtdKontos, setRtdKontos] = React.useState<RtdKonto[]>([]);
    const [loadingRtdKontos, setLoadingRtdKontos] = React.useState(true);
    const [isSaving, setIsSaving] = React.useState(false);

    const handleBack = () => {
        navigateTo('/projects');
    };

    // Load RTD bank accounts
    React.useEffect(() => {
        const fetchRtdKontos = async () => {
            try {
                const response = await fetch('/api/bankaccounts', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Fehler beim Laden der RTD-Konten');
                }

                const data: RtdKonto[] = await response.json();
                setRtdKontos(data);
            } catch (error) {
                console.error('Error fetching RTD accounts:', error);
            } finally {
                setLoadingRtdKontos(false);
            }
        };

        fetchRtdKontos();
    }, [token]);

    const getStatusBadgeClass = (status: string): string => {
        switch (status) {
            case 'OPEN':
                return 'status-badge status-open';
            case 'CLOSED':
                return 'status-badge status-closed';
            case 'ARCHIVED':
                return 'status-badge status-archived';
            default:
                return 'status-badge';
        }
    };

    const getStatusLabel = (status: string): string => {
        switch (status) {
            case 'OPEN':
                return 'Offen';
            case 'CLOSED':
                return 'Geschlossen';
            case 'ARCHIVED':
                return 'Archiviert';
            default:
                return status;
        }
    };

    const getFilenameFromPath = (filepath: string): string => {
        return filepath.split('/').pop() || filepath;
    };

    const handleDownloadFile = async (fileId: number, filename: string) => {
        try {
            const response = await fetch(`/api/projects/files/${fileId}/download`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Download fehlgeschlagen');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    const handleEditClick = () => {
        setEditedName(project.name);
        setEditedStatus(project.status);
        setEditedRtdKontoId(project.rtdKontoId);
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditedName('');
        setEditedStatus('');
        setEditedRtdKontoId(null);
    };

    const handleSaveEdit = async () => {
        if (!editedName.trim()) {
            return;
        }

        setIsSaving(true);
        try {
            const response = await fetch(`/api/projects/${project.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: editedName,
                    status: editedStatus,
                    rtdKontoId: editedRtdKontoId
                }),
            });

            if (!response.ok) {
                throw new Error('Fehler beim Aktualisieren des Projekts');
            }

            // Refetch the project to get the updated rtdKonto relation
            const refetchResponse = await fetch(`/api/projects/${project.id}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (refetchResponse.ok) {
                const updatedProject: Project = await refetchResponse.json();
                onProjectUpdate(updatedProject);
            }

            setIsEditing(false);
        } catch (error) {
            console.error('Error updating project:', error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <div className="project-details-header">
                <button className="back-button" onClick={handleBack} aria-label="Back to projects">
                    ←
                </button>
                <h1>Projektdetails: {project.name}</h1>
            </div>

            <div className="project-info-card">
                {isEditing ? (
                    // Edit mode
                    <>
                        <div className="project-info-header">
                            <h2>Projekt bearbeiten</h2>
                        </div>
                        <div className="edit-form">
                            <div className="form-group">
                                <label htmlFor="edit-project-name">Projektname *</label>
                                <input
                                    id="edit-project-name"
                                    type="text"
                                    value={editedName}
                                    onChange={(e) => setEditedName(e.target.value)}
                                    placeholder="Projektname"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="edit-project-status">Status *</label>
                                <select
                                    id="edit-project-status"
                                    value={editedStatus}
                                    onChange={(e) => setEditedStatus(e.target.value)}
                                >
                                    <option value="OPEN">Offen</option>
                                    <option value="CLOSED">Geschlossen</option>
                                    <option value="ARCHIVED">Archiviert</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="edit-rtd-konto">RTD Bankkonto</label>
                                <select
                                    id="edit-rtd-konto"
                                    value={editedRtdKontoId || ''}
                                    onChange={(e) => setEditedRtdKontoId(e.target.value ? Number(e.target.value) : null)}
                                    disabled={loadingRtdKontos}
                                >
                                    <option value="">Kein Konto ausgewählt</option>
                                    {rtdKontos.map(konto => (
                                        <option key={konto.id} value={konto.id}>
                                            {konto.accountName} ({konto.iban})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="edit-actions">
                                <button 
                                    className="cancel-button" 
                                    onClick={handleCancelEdit}
                                    disabled={isSaving}
                                >
                                    Abbrechen
                                </button>
                                <button 
                                    className="save-button" 
                                    onClick={handleSaveEdit}
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Speichert...' : 'Speichern'}
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    // View mode
                    <>
                        <div className="project-info-header">
                            <h2>{project.name}</h2>
                            <div className="project-header-actions">
                                <span className={getStatusBadgeClass(project.status)}>
                                    {getStatusLabel(project.status)}
                                </span>
                                <button className="edit-button" onClick={handleEditClick}>
                                    ✏️ Bearbeiten
                                </button>
                            </div>
                        </div>
                        {project.description && (
                            <div className="project-description">
                                <p>{project.description}</p>
                            </div>
                        )}
                        {project.rtdKonto && (
                            <div className="project-rtd-account">
                                <h4>RTD Bankkonto:</h4>
                                <p>
                                    <strong>{project.rtdKonto.accountName}</strong> ({project.rtdKonto.institute})
                                    <br />
                                    IBAN: {project.rtdKonto.iban}
                                    <br />
                                    BIC: {project.rtdKonto.bic}
                                </p>
                            </div>
                        )}
                    </>
                )}
                {project.projectFiles && project.projectFiles.length > 0 && (
                    <div className="project-files">
                        <h4>Projektdateien:</h4>
                        <ul className="project-files-list">
                            {project.projectFiles.map((file) => (
                                <li key={file.id} className="project-file-item">
                                    <span className="file-name">{getFilenameFromPath(file.filepath)}</span>
                                    <button
                                        className="download-file-btn"
                                        onClick={() => handleDownloadFile(file.id, getFilenameFromPath(file.filepath))}
                                        title="Datei herunterladen"
                                    >
                                        ⬇ Download
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {!isEditing && (
                    <>
                        <PdfStatusSection projectId={project.id.toString()} token={token} refreshTrigger={refreshTrigger} />
                        <ExportActionsSection projectId={project.id.toString()} projectName={project.name} token={token} />
                    </>
                )}
            </div>
        </>
    );
};

export default ProjectDetailsHeader;
